import { useCallback, useEffect, useRef, useState } from "react";
import type { Document } from "@/types/documents";
import type { FileOutputChipFile } from "./FileOutputChip";

export function decodeBase64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export interface UseGeneratedFileViewerReturn {
  isOpen: boolean;
  document: Document | null;
  blobUrl: string | null;
  blobData: Blob | null;
  openViewer: (file: FileOutputChipFile) => void;
  setIsOpen: (open: boolean) => void;
}

export function useGeneratedFileViewer(): UseGeneratedFileViewerReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [document, setDocument] = useState<Document | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobData, setBlobData] = useState<Blob | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const closeAndCleanup = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setBlobUrl(null);
    setBlobData(null);
    setDocument(null);
  }, []);

  const openViewer = useCallback((file: FileOutputChipFile) => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);

    const bytes = decodeBase64ToBytes(file.dataB64);
    const blob = new Blob([bytes], { type: file.mime || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    blobUrlRef.current = url;

    const synthetic: Document = {
      id: file.id,
      filename: file.filename,
      file_path: file.id,
      building_id: "chatbot-generated",
      content_type: file.mime || "application/octet-stream",
      size: file.sizeBytes,
      created_at: new Date().toISOString(),
    };

    setBlobUrl(url);
    setBlobData(blob);
    setDocument(synthetic);
    setIsOpen(true);
  }, []);

  const handleSetIsOpen = useCallback(
    (open: boolean) => {
      if (open) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
        closeAndCleanup();
      }
    },
    [closeAndCleanup]
  );

  return { isOpen, document, blobUrl, blobData, openViewer, setIsOpen: handleSetIsOpen };
}
